import { instanceOfErrorListResponse } from '@genial/services';
import { ErrorListResponse } from '@genial/services/src';
import { useFlags } from '@unleash/proxy-client-react';
import { useCallback, useMemo, useState } from 'react';

import { SuitabilityExceedModal } from '../../components/SuitabilityExceedModal';
import { checkSuitabilityPerProduct } from '../../store/customer/functions';
import { customerWalletPointsQuery } from '../../store/customer/selector';
import { AssetSelectedTypeMap } from '../../store/product/types';
import { FeatureToggle, useUnleashFlags } from '../../utils/useUnleashFlags';
import { useRecoilQuery } from '../useRecoilQuery';
import { useToastCreator } from '../useToastCreator';

import {
  defaultSuitabilityErrorMessage,
  shouldSkipValidation,
  suitabilityPropertiesMap,
} from './constants';

export const useSuitabilityCheck = (
  accountNumber: number,
  suitabilityProfileName: string | null | undefined
) => {
  const flags = useFlags();
  const { showToast, showToastFromError } = useToastCreator('suitability-check');
  const [maxSuitabilityAmount, setMaxSuitabilityAmount] = useState<number | null>(null);
  const [valueAdjusted, setValueAdjusted] = useState(false);
  const { b2bShouldCheckSuitabilityToInvest } = useUnleashFlags(
    FeatureToggle.ShouldCheckSuitabilityToInvest
  );

  const { data, error } = useRecoilQuery(customerWalletPointsQuery({ accountNumber }), {
    if: () => b2bShouldCheckSuitabilityToInvest,
  });

  const [isCheckingSuitability, setIsCheckingSuitability] = useState(false);
  const [suitabilityExceededVisible, setSuitabilityExceededVisible] = useState(false);

  const closeSuitabilityExceededModal = useCallback(() => {
    setSuitabilityExceededVisible(false);
  }, []);

  const handleAdjustValue = useCallback(() => {
    setSuitabilityExceededVisible(false);
    setValueAdjusted(true);
  }, []);

  const ModalComponent = useMemo(() => {
    return (
      <SuitabilityExceedModal
        currentPoints={data?.currentPoints ?? '--'}
        limitPoints={data?.limitPoints ?? '--'}
        isOpen={suitabilityExceededVisible}
        onClose={closeSuitabilityExceededModal}
        onAdjustValue={handleAdjustValue}
        hasError={!!error || !data}
        maxSuitabilityAmount={maxSuitabilityAmount}
      />
    );
  }, [
    suitabilityExceededVisible,
    closeSuitabilityExceededModal,
    handleAdjustValue,
    data,
    error,
    maxSuitabilityAmount,
  ]);

  const checkSuitability = useCallback(
    (
      productType: keyof AssetSelectedTypeMap,
      offer: AssetSelectedTypeMap[keyof AssetSelectedTypeMap],
      onSuccess: () => void
    ) => {
      const shouldSkip = shouldSkipValidation(productType, flags);
      if (shouldSkip || !b2bShouldCheckSuitabilityToInvest) {
        onSuccess();
        return;
      }
      setIsCheckingSuitability(true);
      const getter = suitabilityPropertiesMap[productType];

      checkSuitabilityPerProduct({
        accountNumber,
        suitabilityProfileName,
        productType,
        customProps: getter?.(offer as any) ?? [],
      })
        .then((response) => {
          if (response.ableToInvest) {
            onSuccess();
          } else if (response.failureType === 'modal') {
            setMaxSuitabilityAmount(response.maxSuitabilityAmount);
            setSuitabilityExceededVisible(true);
          } else {
            setMaxSuitabilityAmount(response.maxSuitabilityAmount);
            showToast('error', response.toastTitle, response.toastMessage);
          }
        })
        .catch((responseError) => {
          if (instanceOfErrorListResponse(responseError as object)) {
            (responseError as ErrorListResponse).errors?.forEach((errorItem) =>
              showToastFromError(errorItem)
            );
            return;
          }
          showToast(...defaultSuitabilityErrorMessage);
        })
        .finally(() => setIsCheckingSuitability(false));
    },
    [
      accountNumber,
      suitabilityProfileName,
      b2bShouldCheckSuitabilityToInvest,
      flags,
      showToast,
      showToastFromError,
    ]
  );

  return {
    isCheckingSuitability,
    setSuitabilityExceededVisible,
    checkSuitability,
    valueAdjusted,
    ModalComponent,
  };
};
